/**
 * StatusBanner — In-App-Status-Komponente (W0.6)
 *
 * Rendert einen Banner in der Topbar, wenn das Backend active: true meldet.
 * Severity-Mapping: 'warning' → .legacy-banner, 'info' → .hint
 * Dismiss: localStorage-Key ht-status-dismissed-<hash>
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useStatusBanner } from './useStatusBanner'
import { isDismissed, setDismissed } from './dismissUtils'

type LinkProp = {
  href: string
  label: string
}

type StatusBannerProps = {
  link?: LinkProp
}

export function StatusBanner({ link }: StatusBannerProps) {
  const { t } = useTranslation('status')
  const { data } = useStatusBanner()

  // dismissed-State: wird neu berechnet wenn data sich aendert
  const dismissed = data?.active && data.message ? isDismissed(data.message) : false
  const [localDismissed, setLocalDismissed] = useState(false)

  if (!data?.active) return null
  if (dismissed || localDismissed) return null

  const bannerClass = data.severity === 'warning' ? 'legacy-banner' : 'hint'

  function handleDismiss() {
    setDismissed(data!.message)
    setLocalDismissed(true)
  }

  return (
    <div
      className={bannerClass}
      role="status"
      aria-live="polite"
      data-testid="status-banner"
      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
    >
      <span style={{ flex: 1 }}>
        {data.message}
        {link && (
          <>
            {' '}
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              {link.label}
            </a>
          </>
        )}
      </span>
      <button
        onClick={handleDismiss}
        aria-label={t('banner.dismiss')}
        data-testid="status-banner-dismiss"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          lineHeight: 0,
          color: 'inherit',
          flexShrink: 0,
        }}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}

export default StatusBanner
